import { takeLatest, call, put, select } from 'redux-saga/effects';

// Connected React Router
import { push } from 'connected-react-router';

// Services
import { stopsApi } from 'services/apiDigitalTicket/stops';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import PATH from 'constants/basePath';

import * as StopsActions from './actions';
import { StopsTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof StopsActions.listStopsRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando paradas' }));
    }
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(stopsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        StopsActions.listStopsSuccess({
          stopsList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('stops - listStops - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(StopsActions.listStopsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listFull({
  payload,
}: ReturnType<typeof StopsActions.listStopsFullRequest>): any {
  try {
    const { body, filtering, transportOperatorId } = payload;
    if (!filtering) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando paradas' }));
    }

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(stopsApi.get, {
      params: {
        transportOperatorId: transportOperatorId || transportOperator.id,
        ...body,
      },
    });

    if (response.status === 200) {
      yield put(
        StopsActions.listStopsFullSuccess({
          stopsListFull: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('stops - listStops - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(StopsActions.listStopsFullFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listStopsItinerary({
  payload,
}: ReturnType<typeof StopsActions.listStopsItineraryRequest>): any {
  try {
    const { body } = payload;
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando paradas' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(stopsApi.getItineraryStops, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        StopsActions.listStopsItinerarySuccess({
          itineraryStopsList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('stops - listStopsItinerary - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(StopsActions.listStopsItineraryFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
  meta,
}: ReturnType<typeof StopsActions.filterStopsRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(stopsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        StopsActions.filterStopsSuccess({
          stopsList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      meta.promise.resolve(response.data);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('stops - filterStops - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(StopsActions.filterStopsFailure());
    meta.promise.reject(error);
  }
}

function* get({
  payload,
}: ReturnType<typeof StopsActions.getStopRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperatorSelected = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(stopsApi.get, {
      params: { transportOperatorId: transportOperatorSelected.id, ...payload },
    });

    if (response.status === 200 && response.data.id) {
      yield put(StopsActions.getStopSuccess({ stopData: response.data }));
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('stops - get - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(StopsActions.getStopFailure());
    yield put(DialogsActions.setClose());
  }
}

function* getReports({
  payload,
}: ReturnType<typeof StopsActions.getStopReportsRequest>): any {
  try {
    const transportOperatorSelected = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(stopsApi.getReports, {
      params: { transportOperatorId: transportOperatorSelected.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        StopsActions.getStopReportsSuccess({ stopReports: response.data }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('stops - get - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(StopsActions.getStopReportsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
  meta,
}: ReturnType<typeof StopsActions.createStopRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando parada' }));
    const { dispatchOnSuccess } = meta;
    const transportOperatorSelected = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(stopsApi.create, {
      data: { ...payload, transportOperatorId: transportOperatorSelected.id },
    });

    if (response.status === 200) {
      yield put(StopsActions.createStopSuccess({ stopCreated: response.data }));
      yield put(DialogsActions.setClose());
      yield put(DialogsActions.setSecondClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Parada criada com sucesso',
        }),
      );

      if (dispatchOnSuccess) {
        const { action, payload } = dispatchOnSuccess;
        yield put(action(payload));
      }

      yield put(StopsActions.listStopsRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('stops - create - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(StopsActions.createStopFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof StopsActions.updateStopRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando parada' }));
    const transportOperatorSelected = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(stopsApi.update, {
      data: { ...payload, transportOperatorId: transportOperatorSelected.id },
    });

    if (response.status === 200) {
      yield put(StopsActions.updateStopSuccess({ stopData: response.data }));
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Parada alterada com sucesso',
        }),
      );

      yield put(push(PATH.STOPS.BASE_URL));
      yield put(StopsActions.listStopsRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('stops - update - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(StopsActions.updateStopFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof StopsActions.deleteStopRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo parada' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(stopsApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Parada excluída com sucesso',
        }),
      );
      yield put(StopsActions.listStopsRequest({}));
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('stops - remove - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(StopsActions.listStopsFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const stopsSagas = [
  takeLatest(StopsTypes.LIST_STOPS_REQUEST, list),
  takeLatest(StopsTypes.LIST_STOPS_FULL_REQUEST, listFull),
  takeLatest(StopsTypes.LIST_ITINERARY_STOPS_REQUEST, listStopsItinerary),
  takeLatest(StopsTypes.FILTER_STOPS_REQUEST, filter),
  takeLatest(StopsTypes.GET_STOP_REQUEST, get),
  takeLatest(StopsTypes.GET_STOP_REPORTS_REQUEST, getReports),
  takeLatest(StopsTypes.CREATE_STOP_REQUEST, create),
  takeLatest(StopsTypes.UPDATE_STOP_REQUEST, update),
  takeLatest(StopsTypes.DELETE_STOP_REQUEST, remove),
];
